import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { Textarea } from "@babackapp/ui/components/textarea";
import { cn } from "@babackapp/ui/lib/utils";
import {
  ChevronDown,
  ForwardIcon,
  Loader2,
  LucideTestTube2,
  LucideWandSparkles,
} from "lucide-react";
import { useEffect, useState } from "react";

export type AIInputQuery = {
  content: string;
};

export type AIInputProps = {
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  onSubmit: () => void;
};

export function AIInput({
  value,
  onChange,
  isLoading = true,
  onSubmit,
}: AIInputProps) {
  const t = useTranslation("");
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const loadingMessages = [
    t("ask.input.generating"),
    t("ask.input.analyzing"),
    t("ask.input.searching"),
    t("ask.input.processing"),
  ];

  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        setIsVisible(true);
      }, 300);
    }, 2500);

    return () => clearInterval(interval);
  }, [isLoading, loadingMessages.length]);

  return (
    <div className="w-full flex flex-col gap-2">
      <InputExperimentalDisclaimer />
      <InputContainer>
        <div className="flex justify-between items-center px-2 py-1">
          <span className="text-sm inline-flex items-center gap-2">
            {isLoading ? (
              <Loader2 className="size-4 text-gray-400 animate-spin" />
            ) : (
              <ForwardIcon className="size-4 text-primary" />
            )}
            <span
              className={cn(
                "transition-opacity duration-300 text-gray-500",
                isVisible ? "opacity-100" : "opacity-0",
              )}
            >
              {isLoading
                ? loadingMessages[loadingMessageIndex]
                : t("ask.input.returnRequests")}
            </span>
          </span>
          {/* <span className="text-muted-foreground text-xs italic">
            3 queries left today
          </span> */}
        </div>
        <TextareaContainer isLoading={isLoading}>
          <Textarea
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSubmit();
              }
            }}
            className={cn(
              "rounded-xl resize-none outline-none border-none shadow-none ring-none focus:outline-none focus:ring-none focus:border-none focus-visible:ring-0",
              isLoading && "opacity-50 pointer-events-none cursor-wait",
            )}
            placeholder={t("ask.input.placeholder")}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="p-1 flex justify-between w-full">
            <ShopSelector />
            <Button
              size="icon"
              className={cn(
                "rounded-full h-8 w-8",
                isLoading && "opacity-50 pointer-events-none cursor-wait",
              )}
              onClick={() => {
                onChange("");
                onSubmit();
              }}
            >
              {isLoading ? (
                <Loader2 className="size-2 animate-spin" />
              ) : (
                <LucideWandSparkles className="size-2" />
              )}
            </Button>
          </div>
        </TextareaContainer>
      </InputContainer>
    </div>
  );
}

function ShopSelector() {
  return <span />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full text-sm gap-1 text-muted-foreground px-2 py-1 h-auto"
        >
          Return requests <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuCheckboxItem checked>
          Return requests
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem>Help desk</DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem>Settings</DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem>Account</DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function InputContainer({ children }: React.PropsWithChildren) {
  return <div className="rounded-xl bg-muted border p-2">{children}</div>;
}

type TextareaContainerProps = {
  isLoading: boolean;
};

function TextareaContainer({
  children,
  isLoading,
}: React.PropsWithChildren<TextareaContainerProps>) {
  return (
    <div
      className={cn(
        "bg-background shadow-lg rounded-xl relative",
        isLoading && "shadow-none",
      )}
    >
      {children}
    </div>
  );
}

function InputExperimentalDisclaimer() {
  const t = useTranslation("");
  return (
    <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-md flex items-center text-yellow-600 gap-2 p-2">
      <LucideTestTube2 className="text-sm" />
      <p className="text-sm">{t("ask.input.experimental.title")}</p>
    </div>
  );
}
